import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { logError } from "@entrilia/shared/logging";
import { api } from "../../api/client";
import { classifyAccessCheckResult } from "../../auth/accessCheck/classifyAccessCheckResult";
import { isUsableLoadFailure, judgeDocumentFailure } from "../../auth/accessCheck/judgeDocumentFailure";
import { probeDocumentUrl, runBlobProbes, runConnectionChecks } from "../../auth/accessCheck/runConnectionChecks";
import type { ConnectionCheckResult, DocumentLoadFailure } from "../../auth/accessCheck/types";
import { getUrlHostSafe } from "@entrilia/shared/utilities/urlHelper";
import { emitPdfBlobCheckCompleted, emitPdfBlobCheckFailed } from "./pdfBlobCheckTelemetry";
import type { RetryOutcomeKind } from "./pdfBlobCheckTelemetry";
import type { PdfBlobCheckResolvedState } from "./pdfBlobCheckTypes";

interface RetryTelemetry {
  retryAttempts: number;
  retryOutcome: RetryOutcomeKind;
}

interface RunPdfBlobCheckDiagnosticsArgs {
  fileSrc: string;
  documentViewSessionId?: string;
  documentProbe?: ConnectionCheckResult;
  loadFailure?: DocumentLoadFailure;
  retry?: RetryTelemetry;
}

const getBlobProbes = withErrorHandling(api.troubleshooting.getBlobProbes);

const isInternetReachable = (checks: ConnectionCheckResult[]): boolean =>
  checks.find((check) => check.id === "internet")?.ok ?? false;

const failedState = (fileSrc: string, documentViewSessionId: string | undefined): PdfBlobCheckResolvedState => {
  emitPdfBlobCheckFailed({ fileSrc, documentViewSessionId });
  return { kind: "failed" };
};

const succeededState = (
  fileSrc: string,
  documentViewSessionId: string | undefined,
  checks: ConnectionCheckResult[],
  loadFailure: DocumentLoadFailure | undefined,
  retry: RetryTelemetry | undefined
): PdfBlobCheckResolvedState => {
  const classification = classifyAccessCheckResult({ checks, context: "document", documentLoadError: loadFailure });
  const documentCheck = checks.find((check) => check.id === "document");
  const judgedOrigin = documentCheck || loadFailure ? judgeDocumentFailure(loadFailure, documentCheck) : undefined;
  // A network verdict needs the corroborating probes — without internet it is a guess.
  const documentFailureOrigin =
    judgedOrigin === "network-blocked" && !isInternetReachable(checks) ? "unknown" : judgedOrigin;
  emitPdfBlobCheckCompleted({
    classification,
    checks,
    fileSrc,
    documentViewSessionId,
    retry,
    loadFailure,
    documentFailureOrigin,
  });
  return { kind: "results", classification, checks, documentFailureOrigin, loadFailure };
};

const documentCheckFromLoadFailure = (fileSrc: string, failure: DocumentLoadFailure): ConnectionCheckResult => ({
  id: "document",
  label: "This document",
  targetHost: getUrlHostSafe(fileSrc),
  ok: false,
  durationMs: 0,
  errorMessage:
    failure.statusCode !== undefined ? `HTTP ${failure.statusCode}` : `${failure.errorName}: ${failure.errorMessage}`,
  failureDetails: {
    url: fileSrc,
    statusCode: failure.statusCode,
    errorName: failure.errorName,
    errorMessage: failure.errorMessage,
  },
});

// Blob probes carry the host-level signal; the real load failure (or a fallback probe) tells Azure errors from proxy blocks.
export const runPdfBlobCheckDiagnostics = async ({
  fileSrc,
  documentViewSessionId,
  documentProbe,
  loadFailure,
  retry,
}: RunPdfBlobCheckDiagnosticsArgs): Promise<PdfBlobCheckResolvedState> => {
  let connectionChecks: ConnectionCheckResult[];
  try {
    connectionChecks = await runConnectionChecks();
  } catch (error) {
    logError(error, "[pdfBlobCheckDiagnostics] runConnectionChecks");
    return failedState(fileSrc, documentViewSessionId);
  }
  if (connectionChecks.length === 0) {
    return failedState(fileSrc, documentViewSessionId);
  }

  if (!isInternetReachable(connectionChecks)) {
    return succeededState(fileSrc, documentViewSessionId, connectionChecks, loadFailure, retry);
  }

  const [targets, apiError] = await getBlobProbes();
  if (apiError) {
    logError(apiError, "[pdfBlobCheckDiagnostics] getBlobProbes");
    return failedState(fileSrc, documentViewSessionId);
  }
  if (targets.length === 0) {
    return failedState(fileSrc, documentViewSessionId);
  }

  let blobChecks: ConnectionCheckResult[];
  try {
    blobChecks = await runBlobProbes(targets);
  } catch (error) {
    logError(error, "[pdfBlobCheckDiagnostics] runBlobProbes");
    return failedState(fileSrc, documentViewSessionId);
  }
  if (blobChecks.length === 0) {
    return failedState(fileSrc, documentViewSessionId);
  }

  const documentCheck =
    documentProbe ??
    (loadFailure && isUsableLoadFailure(loadFailure)
      ? documentCheckFromLoadFailure(fileSrc, loadFailure)
      : await probeDocumentUrl(fileSrc));
  return succeededState(
    fileSrc,
    documentViewSessionId,
    [...connectionChecks, ...blobChecks, documentCheck],
    loadFailure,
    retry
  );
};
